import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { toast } from 'sonner';

import { AdminHeader } from '@/features/admin/ui';
import { adminQueries, auditLog } from '@/lib/queries/admin';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/admin/picks')({
  component: PicksPage,
});

function PicksPage() {
  const qc = useQueryClient();
  const list = useQuery(adminQueries.picks());
  const rows = list.data ?? [];

  const unflag = useMutation({
    mutationFn: async (row: (typeof rows)[number]) => {
      const { error } = await supabase
        .from('content')
        .update({ is_editors_pick: false, editors_pick_at: null, editors_pick_by: null })
        .eq('id', row.id);
      if (error) throw error;
      await auditLog('content.pick_unset', 'content', row.id, { title: row.title });
    },
    onSuccess: () => {
      toast.success('Removed from editor’s picks.');
      void qc.invalidateQueries({ queryKey: ['admin', 'picks'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'content'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed.'),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <AdminHeader kicker="Admin · Curation" title="Editor's Picks" />
      <p className="mt-4 font-sans text-sm text-ink/60">
        Picks are a quiet editorial signal — no countdown, no expiry. They surface a piece on
        discovery until an editor removes the flag.
      </p>

      {list.isLoading ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">
          Nothing is flagged. Mark a pick from a published piece in Content Review.
        </p>
      ) : (
        <ul className="mt-6 divide-y divide-ink/10">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-4 py-4">
              <div className="min-w-0">
                <p className="font-serif text-base text-ink">{r.title}</p>
                <p className="font-sans text-xs text-ink/50">
                  {r.creator?.display_name ?? 'Unknown'} · picked{' '}
                  {r.editors_pick_at ? new Date(r.editors_pick_at).toLocaleDateString() : '—'}
                </p>
              </div>
              <button
                type="button"
                disabled={unflag.isPending}
                onClick={() => unflag.mutate(r)}
                className="shrink-0 rounded-md border border-ink/20 px-3 py-1.5 font-sans text-xs font-medium uppercase tracking-[0.12em] text-ink hover:border-oxblood hover:text-oxblood disabled:opacity-60"
              >
                Unflag
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
